import "./ReportTargetData.scss";
import { MouseEvent, ChangeEvent, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../store/reducers";
import { cloneDeep } from "lodash";
import {
  Button,
  Chip,
  FormControlLabel,
  Popover,
  Radio,
  RadioGroup,
  Skeleton,
  Typography
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { JobPricingPercentileData } from "../../../helpers/models/JobPricingDetailModels";
import { JobPricingReportConstants } from "../../../constants/JobPricingReportConstants";
import {
  getReportDetailsList,
  setDefaultTargetData,
  updateReportDetails
} from "../../../store/slices/jobPricingDetailsSlice";
import { updateJobPricingPayloads } from "../../../store/slices/jobPricingSearchSlice";

import { useTranslation } from "react-i18next";

export const ReportTargetData = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const {
    percentileDataList,
    targetData,
    reportDetails,
    showSelectedPercentileList,
    isPercentileDataLoading
  } = useSelector((state: RootState) => state.jobPricingDetails);
  const { jobPricingPayloads } = useSelector(
    (state: RootState) => state.jobPricingSearch
  );
  const [selectedTargetData, setSelectedTargetData] = useState<string>(
    targetData?.percentileDisplay || ""
  );
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isMoreDataClicked, setIsMoreDataClicked] = useState<boolean>(false);

  const handleMoreTargetDataSelection = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    setIsMoreDataClicked(true);
    setSelectedTargetData((event.target as HTMLInputElement).value);
    handleCloseMoreData();
  };

  const handleOpenMoreData = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setIsOpen((preState: boolean) => !preState);
  };

  const handleCloseMoreData = () => {
    setAnchorEl(null);
    setIsOpen((preState: boolean) => !preState);
  };

  const updatedReportDetails = (selectedTarget: JobPricingPercentileData) => {
    const existingPercentileData = jobPricingPayloads.marketPosition.split(",");
    if (existingPercentileData.includes(selectedTarget.percentile)) {
      const updatedReportDetails = getReportDetailsList(
        cloneDeep(reportDetails),
        selectedTarget
      );
      dispatch(updateReportDetails(updatedReportDetails));
    } else {
      const selectedPercentileData = showSelectedPercentileList.map(
        (data: JobPricingPercentileData) => data.percentile
      );
      const updatedJobPricingPayloads = {
        marketPosition: [...selectedPercentileData, selectedTarget.percentile]
          .sort()
          .join()
      };
      dispatch(updateJobPricingPayloads(updatedJobPricingPayloads));
    }
  };

  useEffect(() => {
    if (targetData && targetData.percentileDisplay) {
      setIsMoreDataClicked(
        !JobPricingReportConstants.DEFAULT_PERCENTILE_VALUES.includes(
          targetData.percentile
        )
      );
      setSelectedTargetData(targetData.percentileDisplay);
    }
  }, [targetData]);

  return (
    <div className="sjpr-target-data-container">
      <Typography variant="body2">
        <b>{t("pa.target")}:</b>
      </Typography>
      {isPercentileDataLoading ? (
        JobPricingReportConstants.DEFAULT_PERCENTILE_LABELS.map(
          (value: string, index: number) => (
            <Skeleton
              key={index}
              variant="rounded"
              width={50}
              height={20}
              sx={{ borderRadius: 50, marginLeft: 2 }}
            />
          )
        )
      ) : (
        <>
          {percentileDataList &&
            percentileDataList.length > 0 &&
            percentileDataList
              .filter((data: JobPricingPercentileData, index: number) =>
                JobPricingReportConstants.DEFAULT_PERCENTILE_VALUES.includes(
                  data.percentile
                )
              )
              .map((data: JobPricingPercentileData, index: number) => (
                <Chip
                  key={"target-data" + index}
                  label={data.percentileDisplay}
                  sx={{ marginLeft: 2 }}
                  color={
                    selectedTargetData === data.percentileDisplay
                      ? "primary"
                      : "default"
                  }
                  onClick={(e) => {
                    setIsMoreDataClicked(false);
                    setSelectedTargetData(data.percentileDisplay);
                    dispatch(setDefaultTargetData(data));
                    updatedReportDetails(data);
                  }}
                />
              ))}
          {percentileDataList && percentileDataList.length > 5 && (
            <>
              <Button
                variant="text"
                color={
                  isMoreDataClicked && selectedTargetData
                    ? "primary"
                    : "inherit"
                }
                endIcon={<ExpandMoreIcon />}
                onClick={(e) => handleOpenMoreData(e)}
                sx={{ marginLeft: 2 }}
              >
                <span>
                  {isMoreDataClicked && selectedTargetData
                    ? selectedTargetData
                    : t("pa.more")}
                </span>
              </Button>
              <Popover
                id="more-data-popover"
                open={isOpen}
                anchorEl={anchorEl}
                onClose={handleCloseMoreData}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left"
                }}
                className="more-target-data-popover"
              >
                <RadioGroup
                  aria-labelledby="more-target-data-radio-buttons-group-label"
                  name="more-target-data-radio-buttons-group"
                  value={selectedTargetData}
                  onChange={handleMoreTargetDataSelection}
                >
                  {percentileDataList
                    .filter(
                      (data: JobPricingPercentileData, index: number) =>
                        !JobPricingReportConstants.DEFAULT_PERCENTILE_VALUES.includes(
                          data.percentile
                        )
                    )
                    .map((data: JobPricingPercentileData, index: number) => (
                      <FormControlLabel
                        key={"more-target-data" + index}
                        value={data.percentileDisplay}
                        control={<Radio />}
                        label={data.percentileDisplay}
                        onClick={(e) => {
                          dispatch(setDefaultTargetData(data));
                          updatedReportDetails(data);
                        }}
                      />
                    ))}
                </RadioGroup>
              </Popover>
            </>
          )}
        </>
      )}
    </div>
  );
};
